import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';
import ReactCSSTransitionReplace from 'react-css-transition-replace';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';
import NextAction from '../Widget/NextAction';

import { Block, FadeContianer } from '../../components/RadialSlider/Components';
import { Container, Body, Content } from '../DefaultStyles/Details';
import { Footer } from '../DefaultStyles/Shared';
import { isLongText } from '../../components/Shared/commonFunctions';
import { SliderTitle, SliderDescription } from '../../components/RadialSlider/Components';
import CssTransition from '../../components/Transition/CssTransition';
import EmailInterstitial from '../../components/EmailInterstitial/EmailInterstitialPopup';
import RadialSlider from '../../components/RadialSlider/SliderOuter';
import ShareDrawer from '../../components/ShareDrawer';
import { getImageUrl } from '../../utils/imageLoading';

import {
    getExitingCardProps,
    onBackWithinCard,
    onShowEmailInterstitial,
    setExitingCardProps,
    setOverlayPartnerVisible
} from '../Widget/actions';

import { CardTypes, RadialAnswerTypes } from '../../utils/enums';
import { isPortrait } from '../../utils/size';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { copyFor, capitalization } from '../../copy/copy';
import { formatThousands, Placeholders } from '../../utils/fillDynamicText';
import { roundHalf } from '../../utils/radialSlider/helpers';

class RadialSliderDetails extends React.Component {
    constructor() {
        super();
        this.state = {
            displayFeedback: false
        };
        this.revealFeedbackTextTimer = null;
    }

    static propTypes = {
        actionIdentifier: PropTypes.string.isRequired,
        activeAction: PropTypes.object.isRequired,
        card: PropTypes.object.isRequired,
        description: PropTypes.string.isRequired,
        emailInterstitialVisibility: PropTypes.bool.isRequired,
        feedbackDescription: PropTypes.string.isRequired,
        feedbackTitle: PropTypes.string.isRequired,
        hasNextAction: PropTypes.bool.isRequired,
        interpolateAsFloats: PropTypes.bool.isRequired,
        isAnswered: PropTypes.bool.isRequired,
        isPoll: PropTypes.bool.isRequired,
        onBackWithinCard: PropTypes.func.isRequired,
        onShowEmailInterstitial: PropTypes.func.isRequired,
        result: PropTypes.number,
        setExitingCardProps: PropTypes.func.isRequired,
        setOverlayPartnerVisible: PropTypes.func.isRequired,
        showEmailInterstitial: PropTypes.bool.isRequired,
        sliderInterpolationSteps: PropTypes.number,
        sliderValues: PropTypes.shape({
            min: PropTypes.number.isRequired,
            max: PropTypes.number.isRequired
        }),
        title: PropTypes.string.isRequired,
        prevAnswer: PropTypes.number
    };

    interpolateAnswers = steps => {
        const distance = this.props.sliderValues.max - this.props.sliderValues.min;
        const spacing = distance / (steps - 1);
        let arr = [];
        for (let i = 0; i < steps; i++) {
            if (i === 0) {
                // first
                if (this.props.interpolateAsFloats) {
                    arr.push(this.props.sliderValues.min.toFixed(1));
                } else {
                    arr.push(this.props.sliderValues.min);
                }
            } else if (i === steps - 1) {
                // last
                if (this.props.interpolateAsFloats) {
                    arr.push(this.props.sliderValues.max.toFixed(1));
                } else {
                    arr.push(this.props.sliderValues.max);
                }
            } else {
                // everything in between
                if (this.props.interpolateAsFloats) {
                    arr.push(
                        roundHalf(this.props.sliderValues.min + i * spacing).toFixed(1)
                    );
                } else {
                    arr.push(Math.floor(this.props.sliderValues.min + i * spacing));
                }
            }
        }
        return arr;
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (window.location.hash === '#isByobPreview' &&
            this.props.isAnswered && !nextProps.isAnswered) this.setState({displayFeedback: false});
    }

    componentWillUnmount() {
        if (this.revealFeedbackTextTimer) clearTimeout(this.revealFeedbackTextTimer);
    }

    revealFeedbackText = () => {
        this.revealFeedbackTextTimer = setTimeout(() => {
            this.setState({
                displayFeedback: true
            });
            clearTimeout(this.revealFeedbackTextTimer);
        }, 500);
    };

    render() {
        const { displayFeedback } = this.state;
        const {
            actionIdentifier,
            activeAction,
            card,
            description,
            emailInterstitialVisibility,
            feedbackDescription,
            feedbackTitle,
            hasNextAction,
            interpolateAsFloats,
            isAnswered,
            isPoll,
            onBackWithinCard,
            prevAnswer,
            result,
            setExitingCardProps,
            sliderInterpolationSteps,
            sliderValues,
            title
        } = this.props;
        const interpolatedAnswers = this.interpolateAnswers(sliderInterpolationSteps + 1);
        isAnswered && this.revealFeedbackText();

        const appendAnswerResult = (result, title) => {
            if (isPoll) return title;
            if (result === RadialAnswerTypes.correct) {
                return `${copyFor(
                    'feedback',
                    'correct',
                    'speedometer',
                    capitalization.first
                )}. ${title}`;
            }
            if (result === RadialAnswerTypes.almost) {
                return `${copyFor(
                    'feedback',
                    'almost',
                    'speedometer',
                    capitalization.first
                )}. ${title}`;
            }
            if (result === RadialAnswerTypes.incorrect) {
                return `${copyFor(
                    'feedback',
                    'incorrect',
                    'speedometer',
                    capitalization.first
                )}. ${title}`;
            } else {
                return title;
            }
        };

        const titleCopy = displayFeedback
            ? appendAnswerResult(result, feedbackTitle)
            : title;
        return (
            <Container>
                <Overlay />
                <OverlayPublisher transitionType="right-to-left" />
                <OverlayPartner transitionType="right-to-left" />
                <Body>
                    <WidgetHeader />
                    <Content radialSlider>
                        <Block>
                            <ReactCSSTransitionReplace
                                transitionName="cross-fade"
                                transitionEnterTimeout={500}
                                transitionLeaveTimeout={500}
                            >
                                <FadeContianer key={`transition-${displayFeedback}`}>
                                    <SliderTitle
                                        scaleFactor={isPortrait() ? 0.8 : 1}
                                        isShortText={!isLongText(titleCopy, 15)}
                                        isLongText={isLongText(titleCopy, 30)}
                                        isExtraLongText={isLongText(titleCopy, 55)}
                                        displayFeedback={displayFeedback}
                                        dangerouslySetInnerHTML={{
                                            __html: titleCopy
                                        }}
                                        onClick={e => {
                                            e.preventDefault();
                                            this.props.setOverlayPartnerVisible(
                                                true,
                                                e,
                                                false
                                            );
                                        }}
                                    />
                                    <SliderDescription
                                        displayFeedback={displayFeedback}
                                        dangerouslySetInnerHTML={{
                                            __html: displayFeedback
                                                ? feedbackDescription
                                                : description
                                        }}
                                        onClick={e => {
                                            if (e.target.localName !== "a") e.preventDefault(); //don't prevent user from clicking a tags
                                            this.props.setOverlayPartnerVisible(
                                                true,
                                                e,
                                                false
                                            );
                                        }}
                                    />
                                </FadeContianer>
                            </ReactCSSTransitionReplace>
                        </Block>
                        <Block>
                            <RadialSlider
                                customColor={this.props.customColor}
                                customLogo={this.props.customLogo}
                                d={190}
                                isPoll={isPoll}
                                isAnswered={isAnswered}
                                stepSize={interpolateAsFloats ? 0.5 : null}
                                sliderValues={sliderValues}
                                answers={interpolatedAnswers}
                                hasNextAction={hasNextAction}
                                prevAnswer={prevAnswer}
                                answersLength={interpolatedAnswers.length}
                                onAnswered={() => {
                                    if (this.props.showEmailInterstitial) {
                                        this.props.onShowEmailInterstitial();
                                    }
                                }}
                            />
                        </Block>
                    </Content>
                    {isAnswered && hasNextAction && !emailInterstitialVisibility && (
                        <NextAction
                            beforeAdvancing={() => {
                                this.props.setExitingCardProps(
                                    'radialSlider',
                                    this.props,
                                    activeAction
                                );
                            }}
                            exitingAction={activeAction}
                        />
                    )}
                    <Footer>
                        <ShareDrawer
                            beforeBack={() => {
                                setExitingCardProps(
                                    'radialSlider',
                                    this.props,
                                    activeAction
                                );
                            }}
                            goBack={() => {
                                if (card.type === CardTypes.actionChoice)
                                    onBackWithinCard(actionIdentifier, card, true);
                            }}
                            fullwidth={true}
                            newsletterSignUpVisible={card.type !== CardTypes.actionChoice}
                            hasBackButton={
                                card.type === CardTypes.actionChoice && !isAnswered
                            }
                            backgroundColor={isAnswered ? null : '#ffffff'}
                        />
                    </Footer>
                </Body>
                <TransitionGroup>
                    {this.props.emailInterstitialVisibility && (
                        <CssTransition
                            key="alert"
                            in={this.props.onShowEmailInterstitial}
                        >
                            <EmailInterstitial skipSuccessNavigation={true} />
                        </CssTransition>
                    )}
                </TransitionGroup>
            </Container>
        );
    }
}

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'radialSlider',
            ['/speedometer', '/speedometer/start'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;
        const hasNextAction =
            getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            ) !== null;
        const activeAction = state.widget.activeAction;
        const feedbackCustomVariableCalc =
            state.radialSlider.feedbackCustomVariableCalc || '';
        const correctValue = state.radialSlider.correctValue;
        const userAnswer = state.radialSlider.userAnswer;

        const interpolateAsFloats = () => {
            const { sliderInterpolationSteps, minValue, maxValue } = activeAction;
            return sliderInterpolationSteps > maxValue - minValue ? true : false;
        };
        const defaultLogo = getImageUrl('ab-speedo-icon.png', 'DEFAULT_IMAGES_URL');

        return {
            actionIdentifier: activeAction.identifier,
            activeAction,
            interpolateAsFloats: interpolateAsFloats(),
            answerCount: activeAction.counter,
            card: state.widget.cards[activeAction.order - 1],
            result: state.radialSlider.result,
            description: activeAction.description,
            emailInterstitialVisibility: state.widget.showEmailInterstitial || false,
            feedbackDescription: (activeAction.feedbackText || '')
                .replace(
                    new RegExp(Placeholders.CustomVariableCalc, 'g'),
                    formatThousands(feedbackCustomVariableCalc) || ''
                )
                .replace(
                    new RegExp(Placeholders.RadialCorrectAnswerPlaceholder, 'g'),
                    formatThousands(correctValue) || ''
                )
                .replace(
                    new RegExp(Placeholders.RadialUserAnswerPlaceholder, 'g'),
                    formatThousands(userAnswer) || ''
                )
                .replace(
                    new RegExp(Placeholders.PartnerName, 'g'),
                    activeAction.partner
                        ? '<span id="partnerName">' +
                              activeAction.partner.name +
                              '</span>'
                        : 'Speakable'
                ),
            feedbackTitle: (activeAction.feedbackHeader || '')
                .replace(
                    new RegExp(Placeholders.CustomVariableCalc, 'g'),
                    formatThousands(feedbackCustomVariableCalc) || ''
                )
                .replace(
                    new RegExp(Placeholders.RadialCorrectAnswerPlaceholder, 'g'),
                    formatThousands(correctValue) || ''
                )
                .replace(
                    new RegExp(Placeholders.RadialUserAnswerPlaceholder, 'g'),
                    formatThousands(userAnswer) || ''
                )
                .replace(
                    new RegExp(Placeholders.PartnerName, 'g'),
                    activeAction.partner
                        ? '<span id="partnerName">' +
                              activeAction.partner.name +
                              '</span>'
                        : 'Speakable'
                ),
            hasNextAction,
            isAnswered: state.radialSlider.isAnswered,
            showEmailInterstitial: activeAction.collectEmail > 0,
            skipEndCard: activeAction.skipEndCard,
            sliderInterpolationSteps: Number(activeAction.sliderInterpolationSteps || 5),
            sliderValues: { min: Number(activeAction.minValue || 0), max: Number(activeAction.maxValue || 0) },
            title: activeAction.heading || activeAction.name,
            isPoll: correctValue === null,
            prevAnswer: userAnswer,
            customColor: activeAction.customRadiatingColor,
            customLogo: !!activeAction.customLogoId
                ? getImageUrl(activeAction.customLogoId)
                : defaultLogo,
        };
    },
    {
        onBackWithinCard,
        onShowEmailInterstitial,
        setExitingCardProps,
        setOverlayPartnerVisible
    }
)(RadialSliderDetails);
